import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Clock, FileText, Zap, Search, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { useLoginContext } from '../context/LoginContext';

const HistorySection = styled.div`
  background: #ffffff;
  border-radius: 0.75rem;
  padding: 1.25rem;
  border: 1px solid #f1f5f9;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.625rem;
    margin-top: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-top: 1rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 1rem;
    gap: 0.625rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SectionIcon = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  background: #f1f5f9;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
    color: #475569;
  }
  
  @media (max-width: 768px) {
    width: 2rem;
    height: 2rem;
    
    svg {
      width: 1rem;
      height: 1rem;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 0.375rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'primary' ? '#1562b7' : '#f8fafc'};
  color: ${props => props.variant === 'primary' ? '#ffffff' : '#475569'};
  border: 1px solid ${props => props.variant === 'primary' ? '#1562b7' : '#e2e8f0'};
  border-radius: 0.5rem;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  line-height: 1.2;

  &:hover {
    background: ${props => props.variant === 'primary' ? '#1562b7' : '#f1f5f9'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.4375rem 0.75rem;
    font-size: 0.75rem;
    
    svg {
      width: 0.75rem;
      height: 0.75rem;
    }
  }
  
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const TableWrapper = styled.div`
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  
  @media (max-width: 768px) {
    border-radius: 0.375rem;
  }
`;

const TableContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f8fafc;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  @media (max-width: 768px) {
    max-height: 350px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
  
  @media (max-width: 768px) {
    min-width: 500px;
  }
`;

const TableHeader = styled.thead`
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;

  &:first-child {
    padding-left: 1.25rem;
  }

  &:last-child {
    padding-right: 1.25rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.75rem;
    font-size: 0.625rem;
    
    &:first-child {
      padding-left: 1rem;
    }

    &:last-child {
      padding-right: 1rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.625rem;
    
    &:first-child {
      padding-left: 0.875rem;
    }

    &:last-child {
      padding-right: 0.875rem;
    }
  }
`;

const TableRow = styled.tr`
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  font-size: 0.8125rem;
  color: #475569;
  vertical-align: middle;

  &:first-child {
    padding-left: 1.25rem;
    font-weight: 500;
    color: #1e293b;
  }

  &:last-child {
    padding-right: 1.25rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.75rem;
    font-size: 0.75rem;
    
    &:first-child {
      padding-left: 1rem;
    }

    &:last-child {
      padding-right: 1rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.625rem;
    
    &:first-child {
      padding-left: 0.875rem;
    }

    &:last-child {
      padding-right: 0.875rem;
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.1875rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: capitalize;
  line-height: 1.2;

  ${props => {
    const status = props.status.toLowerCase();

    if (status.includes('completed')) {
      return `
        background: #f0fdf4;
        color: #166534;
        border: 1px solid #bbf7d0;
      `;
    }

    if (status.includes('in progress') || status.includes('in-progress')) {
      return `
        background: #eff6ff;
        color: #1d4ed8;
        border: 1px solid #bfdbfe;
      `;
    }

    if (status.includes('terminated')) {
      return `
        background: #fef2f2;
        color: #dc2626;
        border: 1px solid #fecaca;
      `;
    }

    return `
      background: #f8fafc;
      color: #64748b;
      border: 1px solid #e2e8f0;
    `;
  }}
  
  @media (max-width: 768px) {
    font-size: 0.625rem;
    padding: 0.125rem 0.375rem;
  }
`;

const VulnerabilityDot = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.type) {
      case 'Critical': return '#dc2626';
      case 'High': return '#ea580c';
      case 'Medium': return '#f59e0b';
      case 'Low': return '#10b981';
      default: return '#94a3b8';
    }
  }};
`;

const VulnerabilityCount = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${props => {
    switch (props.type) {
      case 'Critical': return '#dc2626';
      case 'High': return '#ea580c';
      case 'Medium': return '#f59e0b';
      case 'Low': return '#10b981';
      default: return '#64748b';
    }
  }};
  
  @media (max-width: 768px) {
    font-size: 0.625rem;
  }
`;

const VulnerabilityGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.375rem;
  }
`;

const VulnerabilityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1875rem 0.375rem;
  border-radius: 0.375rem;
  background: #f8fafc;
  
  @media (max-width: 768px) {
    padding: 0.125rem 0.25rem;
  }
`;

const TimeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: #64748b;
  font-size: 0.8125rem;
  
  svg {
    width: 0.875rem;
    height: 0.875rem;
    min-width: 0.875rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    gap: 0.25rem;
    
    svg {
      width: 0.75rem;
      height: 0.75rem;
      min-width: 0.75rem;
    }
  }
`;

const EmptyState = styled.div`
  padding: 2rem 1.5rem;
  text-align: center;
  background: #f8fafc;
  border-radius: 0.5rem;
  border: 2px dashed #cbd5e1;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem 0.75rem;
  }
`;

const EmptyStateIcon = styled.div`
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1.25rem;
  background: #f1f5f9;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: #64748b;
  }
  
  @media (max-width: 768px) {
    width: 2.5rem;
    height: 2.5rem;
    margin-bottom: 1rem;
    
    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const EmptyStateText = styled.p`
  color: #64748b;
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const VulnerabilityIndicator = ({ count, type }) => {
  if (count === 0) return null;
  
  return (
    <VulnerabilityItem>
      <VulnerabilityDot type={type} />
      <VulnerabilityCount type={type}>{count}</VulnerabilityCount>
    </VulnerabilityItem>
  );
};

const ScanHistorySectionComponent = () => {
  const navigate = useNavigate();
  const [scanHistory, setScanHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useLoginContext();

  const userTimeZone = user?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      timeZone: userTimeZone,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  useEffect(() => {
    const fetchScanHistory = async () => {
      try {
        const response = await apiRequest('/api/scanhistory', { method: 'GET' });
        if (!response.ok) throw new Error('Failed to fetch scan history');
        const data = await response.json();
        setScanHistory(data.sort((a, b) => new Date(b.time) - new Date(a.time)));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchScanHistory();
  }, []);

  const handleRowClick = reportLink => navigate(reportLink);

  if (loading) {
    return (
      <HistorySection>
        <SectionHeader>
          <HeaderLeft>
            <SectionIcon>
              <Search />
            </SectionIcon>
            <SectionTitle>Recent Activity</SectionTitle>
          </HeaderLeft>
          <ActionGroup>
            <ActionButton onClick={() => navigate('/automated-reports')}>
              <FileText /> Reports
            </ActionButton>
            <ActionButton variant="primary" onClick={() => navigate('/autoscan')}>
              <Zap /> New Scan
            </ActionButton>
          </ActionGroup>
        </SectionHeader>
        
        <EmptyState>
          <EmptyStateIcon>
            <Clock />
          </EmptyStateIcon>
          <EmptyStateTitle>Loading History</EmptyStateTitle>
          <EmptyStateText>
            Fetching your scan history...
          </EmptyStateText>
        </EmptyState>
      </HistorySection>
    );
  }

  if (error) {
    return (
      <HistorySection>
        <SectionHeader>
          <HeaderLeft>
            <SectionIcon>
              <Search />
            </SectionIcon>
            <SectionTitle>Recent Activity</SectionTitle>
          </HeaderLeft>
          <ActionGroup>
            <ActionButton onClick={() => navigate('/automated-reports')}>
              <FileText /> Reports
            </ActionButton>
            <ActionButton variant="primary" onClick={() => navigate('/autoscan')}>
              <Zap /> New Scan
            </ActionButton>
          </ActionGroup>
        </SectionHeader>
        
        <EmptyState>
          <EmptyStateIcon>
            <AlertTriangle />
          </EmptyStateIcon>
          <EmptyStateTitle>Error Loading History</EmptyStateTitle>
          <EmptyStateText>
            {error}
          </EmptyStateText>
        </EmptyState>
      </HistorySection>
    );
  }

  if (scanHistory.length === 0) {
    return (
      <HistorySection>
        <SectionHeader>
          <HeaderLeft>
            <SectionIcon>
              <Search />
            </SectionIcon>
            <SectionTitle>Recent Activity</SectionTitle>
          </HeaderLeft>
          <ActionGroup>
            <ActionButton onClick={() => navigate('/automated-reports')}>
              <FileText /> Reports
            </ActionButton>
            <ActionButton variant="primary" onClick={() => navigate('/autoscan')}>
              <Zap /> New Scan
            </ActionButton>
          </ActionGroup>
        </SectionHeader>
        
        <EmptyState>
          <EmptyStateIcon>
            <Search />
          </EmptyStateIcon>
          <EmptyStateTitle>No Scans Yet</EmptyStateTitle>
          <EmptyStateText>
            Your scan history will appear here once you run your first security assessment.
            Start by conducting a comprehensive scan of your assets.
          </EmptyStateText>
          <ActionButton variant="primary" onClick={() => navigate('/autoscan')}>
            <Zap /> Start Your First Scan
          </ActionButton>
        </EmptyState>
      </HistorySection>
    );
  }

  return (
    <HistorySection>
      <SectionHeader>
        <HeaderLeft>
          <SectionIcon>
            <Search />
          </SectionIcon>
          <SectionTitle>Recent Activity</SectionTitle>
        </HeaderLeft>
        <ActionGroup>
          <ActionButton onClick={() => navigate('/automated-reports')}>
            <FileText /> View Reports
          </ActionButton>
          <ActionButton variant="primary" onClick={() => navigate('/autoscan')}>
            <Zap /> New Scan
          </ActionButton>
        </ActionGroup>
      </SectionHeader>

      <TableWrapper>
        <TableContainer>
          <Table>
            <TableHeader>
              <tr>
                <HeaderCell>Asset</HeaderCell>
                <HeaderCell>Target</HeaderCell>
                <HeaderCell>Scan Time</HeaderCell>
                <HeaderCell>Duration</HeaderCell>
                <HeaderCell>Status</HeaderCell>
                <HeaderCell>Vulnerabilities</HeaderCell>
              </tr>
            </TableHeader>
            <tbody>
              {scanHistory.map(scan => (
                <TableRow 
                  key={scan.id} 
                  onClick={() => handleRowClick(scan.reportLink)}
                >
                  <TableCell>
                    {scan.asset?.name ?? scan.type.charAt(0).toUpperCase() + scan.type.slice(1)}
                  </TableCell>
                  <TableCell>{scan.target}</TableCell>
                  <TableCell>
                    <TimeInfo>
                      <Clock />
                      {formatDate(scan.time)}
                    </TimeInfo>
                  </TableCell>
                  <TableCell>{scan.duration}</TableCell>
                  <TableCell>
                    <StatusBadge status={scan.status}>
                      {scan.status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {scan.vulnerabilityCounts && (
                      <VulnerabilityGroup>
                        <VulnerabilityIndicator count={scan.vulnerabilityCounts.Critical} type="Critical" />
                        <VulnerabilityIndicator count={scan.vulnerabilityCounts.High} type="High" />
                        <VulnerabilityIndicator count={scan.vulnerabilityCounts.Medium} type="Medium" />
                        <VulnerabilityIndicator count={scan.vulnerabilityCounts.Low} type="Low" />
                      </VulnerabilityGroup>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </TableWrapper>
    </HistorySection>
  );
};

export default ScanHistorySectionComponent;